'use client';

import { useAccount } from 'wagmi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LngLat, LngLatBounds } from 'maplibre-gl';
import { BlockPlaces } from '@ivyroot/block-places';
import SlapMap from '@/app/map/SlapMap';
import SlapExecutooor from '@/app/slaps/SlapExecutooor';
import SlapperButtonSection from '@/app/slaps/SlapperButtonSection';
import SlapCheckWarnings, { SlapWarning } from '@/app/slaps/SlapCheckWarnings';
import ConnectAccount from '@/app/components/ConnectAccount';
import GameTitle from '@/app/components/GameTitle';
import GameTitleMobile from '@/app/components/GameTitleMobile';
import StickerReviewer from '@/app/stickers/StickerReviewer';
import {
  Board,
  DisplayedCenter,
  FocusedPlace,
  PendingSlapList,
  Slap,
  SlapId,
  StickerDesign,
  StickerDesignId,
  ExpandedSlap,
  convertExpandedSlapToSlap,
  defaultDisplayedCenter,
  emptyBoard,
} from '@/app/utils/Primitives';
import { getPlace } from '@/app/places/Places';
import { getCurrentTimestamp, formatEthAddress } from '@/app/utils/GeneralUtilities';
import { getObjectives } from '@/app/objectives/Objectives';
import {
  getStickerDesign,
  writeStickerDesignToBoard,
} from '@/app/stickers/StickerDesigns';
import {
  deleteSlapFromBoard,
  setStoredSlapSubmitted,
  writeSlapToBoard,
} from '@/app/slaps/Slaps';
import { useFetchSavePaymentMethods } from '@/app/payments/PaymentMethods';

type MapViewTarget = {
  center: [number, number];
  zoom?: number;
  bounds?: LngLatBounds | null;
};

type LockedSlapRailProps = {
  sticker: StickerDesign;
  slap?: Slap | null;
  initialPlaceId?: number | null;
  sourceLabel?: string;
  viewMode?: 'sticker' | 'slap';
};

function computeInitialCenter(placeId: number | null): DisplayedCenter {
  if (!placeId) {
    return defaultDisplayedCenter;
  }
  const bounds = BlockPlaces.lngLatBoundsFromPlaceId(placeId);
  if (!bounds) {
    return defaultDisplayedCenter;
  }
  const center = bounds.getCenter();
  const centerLngLat = new LngLat(center.lng, center.lat);
  return {
    center: centerLngLat,
    zoom: 13,
    placeId,
    bounds,
  };
}

function formatPrice(price: number) {
  if (price === 0) return 'Free';
  return `${price} ETH`;
}

export default function LockedSlapRail({
  sticker,
  slap,
  initialPlaceId,
  sourceLabel,
  viewMode,
}: LockedSlapRailProps) {
  const { address: playerAddress } = useAccount();
  const boardRef = useRef<Board>(emptyBoard);
  const [clientDisplayedCenter, setClientDisplayedCenter] = useState<DisplayedCenter>(
    () => computeInitialCenter(initialPlaceId ?? null)
  );
  const [focusedPlace, setFocusedPlace] = useState<FocusedPlace>({
    placeId: initialPlaceId ?? null,
    source: initialPlaceId ? 'url' : undefined,
  });
  const [draftSlaps, setDraftSlaps] = useState<PendingSlapList>([]);
  const [deletedSlaps, setDeletedSlaps] = useState<SlapId[]>([]);
  const [latestSlap, setLatestSlap] = useState<Slap | null>(slap ?? null);
  const [latestUpdateTime, setLatestUpdateTime] = useState<number | null>(null);
  const [slapExecutorOpenTimestamp, setSlapExecutorOpenTimestamp] = useState<number>(0);
  const [currentStickerId] = useState<StickerDesignId>(sticker.id);
  const [currentSticker, setCurrentSticker] = useState<StickerDesign | null>(sticker);
  const [currentSlapSize, setCurrentSlapSize] = useState<number>(1);
  const [lockedMapView, setLockedMapView] = useState<MapViewTarget | null>(null);
  const [mapLocked, setMapLocked] = useState(false);
  const [isCoveredSlap, setIsCoveredSlap] = useState(false);
  const [slapsSyncTick, setSlapsSyncTick] = useState(0);
  const [reviewStickerId, setReviewStickerId] = useState<number | null>(null);
  const [slapPreCheckWarnings, setSlapPreCheckWarnings] = useState<SlapWarning[]>([]);
  const lastDraftIdRef = useRef<SlapId | null>(null);
  const draftCenterRef = useRef<[number, number] | null>(null);

  useFetchSavePaymentMethods({ boardRef });

  useEffect(() => {
    writeStickerDesignToBoard({ boardRef, sticker });
    setCurrentSticker(sticker);
  }, [sticker]);

  useEffect(() => {
    if (!slap) {
      return;
    }
    writeSlapToBoard({ boardRef, slap });
  }, [slap]);

  const activePlaceId =
    focusedPlace.placeId ??
    clientDisplayedCenter.placeId ??
    initialPlaceId ??
    null;

  const handleSetFocusOnPlace = useCallback((value: FocusedPlace) => {
    setFocusedPlace(value);
  }, []);

  const getNextDraftSlapId = useCallback(() => {
    return Date.now();
  }, []);

  const createDraftSlap = useCallback(
    async (placeId: number, stickerDesign: StickerDesign) => {
      if (draftSlaps.length > 0) {
        return;
      }
      const place = getPlace({ boardRef, placeId });
      if (!place) {
        return;
      }
      const timestamp = getCurrentTimestamp();
      const slapId = getNextDraftSlapId();
      const player = playerAddress ?? '0x0000000000000000000000000000000000000000';

      writeStickerDesignToBoard({ boardRef, sticker: stickerDesign });

      const draftSlap: Slap = {
        id: slapId,
        time: timestamp,
        size: currentSlapSize,
        place: place.id,
        sticker: stickerDesign.id,
        player,
        isDraft: true,
      };
      const expanded: ExpandedSlap = {
        id: slapId,
        time: timestamp,
        size: currentSlapSize,
        place,
        sticker: stickerDesign,
        player,
        isDraft: true,
      };

      await writeSlapToBoard({ boardRef, slap: draftSlap });
      setLatestSlap(draftSlap);
      setDraftSlaps(prev => [...prev, expanded]);
      setLatestUpdateTime(Date.now());
    },
    [boardRef, currentSlapSize, playerAddress, draftSlaps.length, getNextDraftSlapId]
  );

  const handleDeletePendingSlap = useCallback(
    (slapId: SlapId) => {
      setDraftSlaps(prev => {
        const target = prev.find(slap => slap.id === slapId);
        if (!target) {
          return prev;
        }
        deleteSlapFromBoard({ boardRef, slap: convertExpandedSlapToSlap(target) });
        setDeletedSlaps([slapId]);
        setLatestUpdateTime(Date.now());
        return prev.filter(slap => slap.id !== slapId);
      });
      if (latestSlap?.id === slapId) {
        setLatestSlap(null);
      }
      setSlapExecutorOpenTimestamp(0);
    },
    [boardRef, latestSlap]
  );

  const markDraftsSubmitted = useCallback(() => {
    setDraftSlaps(prev =>
      prev.map(item => {
        setStoredSlapSubmitted({ boardRef, slapId: item.id });
        return { ...item, isSubmitted: true };
      })
    );
  }, [boardRef]);

  const handleDidSubmitTransaction = useCallback(() => {
    markDraftsSubmitted();
    setLatestUpdateTime(Date.now());
  }, [markDraftsSubmitted]);

  const handleSlapAction = useCallback(async () => {
    if (draftSlaps.length > 0) {
      setSlapExecutorOpenTimestamp(Date.now());
      return;
    }
    if (!currentStickerId || !activePlaceId) {
      return;
    }
    const stickerDesign = getStickerDesign({ boardRef, stickerId: currentStickerId });
    if (!stickerDesign) {
      return;
    }
    await createDraftSlap(activePlaceId, stickerDesign);
  }, [draftSlaps.length, currentStickerId, activePlaceId, boardRef, createDraftSlap]);

  const handleSlapCreatorOpen = useCallback(() => {
    void handleSlapAction();
  }, [handleSlapAction]);

  const handleSlapExecutorOpen = useCallback(() => {
    setSlapExecutorOpenTimestamp(Date.now());
  }, []);

  const handleOpenStickerReview = useCallback(() => {
    setReviewStickerId(currentStickerId ?? null);
  }, [currentStickerId]);

  const handleCloseStickerReview = useCallback(() => {
    setReviewStickerId(null);
  }, []);

  const handleSlapsFetched = useCallback(() => {
    setSlapsSyncTick(Date.now());
  }, []);

  const handleCancelDraft = useCallback(() => {
    if (draftSlaps.length === 0) {
      return;
    }
    setSlapPreCheckWarnings([]);
    handleDeletePendingSlap(draftSlaps[0].id);
  }, [draftSlaps, handleDeletePendingSlap, setSlapPreCheckWarnings]);

  const getLockedViewForSlap = useCallback(
    (placeId: number | null, size: number): MapViewTarget | null => {
      if (!placeId) {
        return null;
      }
      const sizedBounds = BlockPlaces.sizedLngLatBoundsFromPlaceId(placeId, size);
      const rawBounds = sizedBounds ?? BlockPlaces.lngLatBoundsFromPlaceId(placeId);
      const padFactor = size <= 1 ? 0.5 : 0.7;
      const bounds = rawBounds
        ? new LngLatBounds(
            [
              rawBounds.getWest() - (rawBounds.getEast() - rawBounds.getWest()) * padFactor,
              rawBounds.getSouth() - (rawBounds.getNorth() - rawBounds.getSouth()) * padFactor,
            ],
            [
              rawBounds.getEast() + (rawBounds.getEast() - rawBounds.getWest()) * padFactor,
              rawBounds.getNorth() + (rawBounds.getNorth() - rawBounds.getSouth()) * padFactor,
            ]
          )
        : null;
      if (!bounds) {
        return null;
      }
      const center = bounds.getCenter();
      return {
        center: [center.lng, center.lat],
        zoom: 14,
        bounds,
      };
    },
    []
  );

  const isSlapView = viewMode ? viewMode === 'slap' : Boolean(slap);
  useEffect(() => {
    const currentDraft = draftSlaps[0] ?? null;
    const previousDraftId = lastDraftIdRef.current;

    if (currentDraft && previousDraftId !== currentDraft.id) {
      const lockedView = getLockedViewForSlap(currentDraft.place.id, currentDraft.size || 1);
      if (lockedView) {
        draftCenterRef.current = lockedView.center;
        setLockedMapView(lockedView);
        setMapLocked(true);
      }
      lastDraftIdRef.current = currentDraft.id;
    } else if (!currentDraft && previousDraftId !== null) {
      const fallbackCenter =
        draftCenterRef.current ||
        (clientDisplayedCenter.center
          ? [clientDisplayedCenter.center.lng, clientDisplayedCenter.center.lat]
          : null);
      if (fallbackCenter) {
        setLockedMapView({ center: fallbackCenter, zoom: 12 });
      } else {
        setLockedMapView(null);
      }
      setMapLocked(false);
      lastDraftIdRef.current = null;
    }
  }, [draftSlaps, clientDisplayedCenter.center, getLockedViewForSlap]);

  useEffect(() => {
    const slapSize = slap?.size ?? 1;
    if (!isSlapView || !slap || slapSize !== 1) {
      setIsCoveredSlap(false);
      return;
    }
    const place = getPlace({ boardRef, placeId: slap.place });
    if (!place || place.slap == null) {
      setIsCoveredSlap(false);
      return;
    }
    setIsCoveredSlap(place.slap !== slap.id);
  }, [isSlapView, slap, boardRef, slapsSyncTick]);
  const headerTitle = isSlapView
    ? `Slap #${slap?.id ?? 'Unknown'} • Sticker #${sticker.id}`
    : `Sticker #${sticker.id}`;
  const headerSubtitle = isSlapView
    ? 'This slap locks in a sticker. Pick a place to slap it again.'
    : 'Pick a place to slap this sticker on the map.';
  const hasPendingSlap = draftSlaps.length > 0;
  const isSlapDisabled = !currentStickerId || !activePlaceId;
  const formattedPrice = useMemo(() => formatPrice(sticker.price), [sticker.price]);
  const helperText = isSlapView
    ? 'Use the map to slap this sticker again!'
    : 'Use the map to slap this sticker anywhere!';

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8">
        <div className="flex flex-col gap-6 rounded-3xl bg-white p-6 shadow-xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="md:hidden">
              <GameTitleMobile logoSrc="/static/logo-main.png" />
            </div>
            <div className="hidden md:block">
              <GameTitle logoSrc="/static/logo-main.png" />
            </div>
            <div className="flex items-center gap-3">
              {playerAddress ? (
                <div className="rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white">
                  {formatEthAddress(playerAddress, 5)}
                </div>
              ) : (
                <ConnectAccount
                  variant="compact"
                  buttonClassName="rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition"
                />
              )}
            </div>
          </div>

          <div className="box-border flex w-fit max-w-full flex-col items-start self-start rounded-2xl bg-slate-50 p-3 text-left md:inline-flex md:p-4">
            <div className="pl-3 text-lg font-semibold text-slate-900">
              {isSlapView ? `Viewing Slap #${slap?.id ?? 'Unknown'}` : 'Viewing Sticker'}
            </div>
            {isSlapView && isCoveredSlap && (
              <div className="pl-3 text-sm text-slate-500">
                Slap has been covered
              </div>
            )}
            <div className="mt-2 w-full max-w-[90vw] md:max-w-[315px]">
              <SlapperButtonSection
                sticker={currentSticker}
                stickerDescription={null}
                onStickerInfoClick={handleOpenStickerReview}
                preCheckWarningsContent={
                  hasPendingSlap && slapPreCheckWarnings.length > 0 ? (
                    <SlapCheckWarnings
                      boardRef={boardRef}
                      warnings={slapPreCheckWarnings}
                      pendingSlaps={draftSlaps}
                      objectives={getObjectives({ boardRef })}
                    />
                  ) : null
                }
                hasPendingSlap={hasPendingSlap}
                hasConnectedWallet={!!playerAddress}
                currentSlapSize={currentSlapSize}
                onSlapSizeChange={setCurrentSlapSize}
                onPickStickers={() => {}}
                onSlapAction={handleSlapAction}
                onCancelDraft={handleCancelDraft}
                isSlapDisabled={isSlapDisabled}
                pendingSlaps={draftSlaps}
                showPickStickers={false}
                className="mt-0 w-full items-start"
                marginTopClassName="mt-0"
                stickerLabel="Sticker"
                splitDesktop
                helperText={helperText}
                helperTextClassName="pl-3"
                helperTextDesktopPosition="above-controls"
              />
            </div>
          </div>
        </div>

        <div className="relative h-[60vh] min-h-[360px] w-full overflow-hidden rounded-3xl bg-white shadow-xl md:h-[70vh]">
          <SlapMap
            boardRef={boardRef}
            latestSlap={latestSlap}
            realtimeLatestSlap={null}
            clientDisplayedCenter={clientDisplayedCenter}
            setClientDisplayedCenter={setClientDisplayedCenter}
            setFocusOnPlace={handleSetFocusOnPlace}
            focusedPlace={focusedPlace}
            handleSlapExecutorOpen={handleSlapExecutorOpen}
            handleSlapCreatorOpen={handleSlapCreatorOpen}
            slapsInProgress={draftSlaps.length > 0 ? draftSlaps : null}
            deletedSlaps={deletedSlaps}
            latestUpdateTime={latestUpdateTime}
            initialPlaceId={initialPlaceId ?? undefined}
            fillContainer
            lockedView={lockedMapView}
            isLocked={mapLocked}
            onSlapsFetched={handleSlapsFetched}
          />
        </div>
      </div>

      <SlapExecutooor
        pendingSlaps={draftSlaps.length > 0 ? [draftSlaps[0]] : null}
        boardRef={boardRef}
        setOpenTimestamp={slapExecutorOpenTimestamp}
        handleDeletePendingSlap={handleDeletePendingSlap}
        handleDidSubmitTransaction={handleDidSubmitTransaction}
        addSlapActivity={() => {}}
        renderTriggerButton={false}
        autoExecuteOnOpen
        onPreCheckWarningsChange={setSlapPreCheckWarnings}
      />
      <StickerReviewer
        stickerId={reviewStickerId}
        boardRef={boardRef}
        onClose={handleCloseStickerReview}
        showActions={false}
        showPublisherLink={false}
      />
    </div>
  );
}
